import InformationCircleIcon from "@heroicons/react/24/outline/InformationCircleIcon";

import cx from "@hsl/core/utils/cx";
import { localeFormat } from "@hsl/fund-explorer/format";
import { type FundDataWithType as FundMetaDataWithType } from "@hsl/fund-explorer/metaSchemas";
import { type FieldMeta, type TopLevelDataObj } from "@hsl/fund-explorer/types";

import useFundFieldMeta, {
    type Props as FundFieldMetaProps,
} from "../../../hooks/useFundFieldMeta";
import { Translate } from "../../../hooks/useTranslation";
import formatMetaValue from "../../../lib/formatMetaValue";
import useExplorerStore from "../../../store/store";
import Tooltip from "../../Tooltip";

export interface Props {
    fund: FundMetaDataWithType;
    tabs?: boolean;
}

const FundSummary = ({ fund, tabs }: Props) => {
    switch (fund.type) {
        case "pmc":
            return (
                <FundSummaryContent
                    fund={fund}
                    tabs={tabs}
                    meta={{
                        type: "pmc",
                        fundFields: [
                            "fundLaunchDate",
                            // Not present in data yet
                            // TODO: uncomment when available
                            // "aum",
                            "domicile",
                            "baseCurrency",
                        ],
                    }}
                />
            );
        case "collective":
            return (
                <FundSummaryContent
                    fund={fund}
                    tabs={tabs}
                    meta={{
                        type: "collective",
                        fundFields: [
                            "aum",
                            "fundLaunchDate",
                            "productType",
                            "domicile",
                            "baseCurrency",
                            "sfdrClassification",
                        ],
                    }}
                />
            );
        case "etf":
            return (
                <FundSummaryContent
                    fund={fund}
                    tabs={tabs}
                    meta={{
                        type: "etf",
                        fundFields: [
                            "fundLaunchDate",
                            "aum",
                            "domicile",
                            "baseCurrency",
                            "sfdrClassification",
                        ],
                    }}
                />
            );
        default:
            return null;
    }
};

const FundSummaryContent = ({
    fund,
    tabs,
    meta,
}: Props & { meta: FundFieldMetaProps }) => {
    const metaData = useFundFieldMeta(meta);
    const locale = useExplorerStore((state) => state.config.locale);

    const metaGuard = (meta: FieldMeta | undefined): meta is FieldMeta =>
        Boolean(meta);

    const toFormattedValue = (meta: FieldMeta) => {
        return {
            ...meta,
            formattedValue: formatMetaValue(
                fund.data as TopLevelDataObj,
                meta,
            ) as string,
            generatedTooltip: summaryItemTooltip(
                fund.data as TopLevelDataObj,
                meta,
            ),
        };
    };

    const metaWithNoValue = (meta: ReturnType<typeof toFormattedValue>) => {
        return (
            typeof meta.formattedValue === "string" &&
            meta.formattedValue.length > 0 &&
            meta.formattedValue !== "-"
        );
    };

    return (
        <div
            className={cx(
                "grid w-full max-w-full grid-cols-3 gap-x-4 gap-y-3 pt-3 text-sm sm:flex sm:gap-x-8 sm:pt-4",
                tabs && "border-platinum border-b pb-3 sm:pb-4",
            )}
        >
            {metaData
                .filter(metaGuard)
                .map(toFormattedValue)
                .filter(metaWithNoValue)
                .map((meta) => {
                    const type = /date/gi.test(meta.code_name)
                        ? "date"
                        : "number";
                    return (
                        <div key={meta.code_name}>
                            <div className="relative w-max max-w-full font-medium">
                                {meta.generatedTooltip ? (
                                    <span className="relative">
                                        <Translate>{meta?.label}</Translate>
                                        {meta.generatedTooltip}
                                    </span>
                                ) : (
                                    <Translate>{meta?.label}</Translate>
                                )}
                            </div>
                            <div>
                                {localeFormat(
                                    type,
                                    locale,
                                    meta.formattedValue,
                                )}
                            </div>
                        </div>
                    );
                })}
        </div>
    );
};

function summaryItemTooltip(
    fund: TopLevelDataObj,
    meta: FieldMeta | undefined,
) {
    if (!meta) {
        return <></>;
    }
    let content = <></>;
    let icon = <></>;
    switch (meta.code_name) {
        case "aum":
            content = (
                <>
                    <Translate>As at</Translate>{" "}
                    {fund.data[`${meta.code_name}__date`]}
                </>
            );
            icon = (
                <InformationCircleIcon className="text-primary h-3.5 w-3.5" />
            );
            break;
        default:
            return <></>;
    }
    return (
        <Tooltip
            content={content}
            placement="right-end"
            className="absolute -right-3 -top-[5px]"
            icon={icon}
            withArrow={false}
        />
    );
}

export default FundSummary;
